import { serverDate } from '../../../helpers/dates';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  effect,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router, RouterModule } from '@angular/router';

import { DropdownComponent } from '../../ui/dropdown/dropdown.component';
import { LocaleService } from '../../../services/locale.service';
import { TPipe } from '../../../pipe/t.pipe';
import { NotificationSocketService } from '../../../services/notification-socket.service';
import { NotificationsService } from '../../../services/notifications.service';
import { PushNotificationsService } from '../../../services/push-notifications.service';
import {
  NOTIFICATION_TYPE_META_SAFE,
  Notification,
  notificationLink,
  notificationText,
} from '../../../types/notification.types';

/** Nombre de notifs affichées dans le menu (la page complète montre tout). */
const PREVIEW_SIZE = 8;

@Component({
  selector: 'app-notification-dropdown',
  templateUrl: './notification-dropdown.component.html',
  imports: [CommonModule, RouterModule, DropdownComponent, TPipe],
})
export class NotificationDropdownComponent implements OnInit {
  private readonly notifications = inject(NotificationsService);
  private readonly socket = inject(NotificationSocketService);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly locale = inject(LocaleService);

  /** Push web (FCM) — exposé au template pour le toggle. */
  readonly push = inject(PushNotificationsService);

  readonly isOpen = signal(false);
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<Notification[]>([]);

  /** Badge live — source de vérité = le service WS (bootstrap REST à l'init). */
  readonly unreadCount = this.socket.unreadCount;

  readonly typeMeta = NOTIFICATION_TYPE_META_SAFE;
  readonly text = notificationText;

  constructor() {
    // Temps réel : une nouvelle notif arrive → prepend dans la liste preview.
    effect(() => {
      const incoming = this.socket.incoming();
      if (!incoming) return;
      this.items.update((list) => {
        const deduped = list.filter((n) => n.id !== incoming.id);
        return [incoming, ...deduped].slice(0, PREVIEW_SIZE);
      });
    });
    // Ack de lecture (depuis un autre onglet / le push serveur) → maj item.
    effect(() => {
      const ack = this.socket.readAck();
      if (!ack) return;
      this.items.update((list) =>
        list.map((n) => (n.id === ack.id ? { ...n, read: true } : n)),
      );
    });
    // Badge : la valeur temps réel vient du push WS `/count`. Le REST
    // `count/unread` ne sert qu'à bootstrapper le badge à chaque (re)connexion
    // WS (init + reconnexion après coupure → rattrape d'éventuels events ratés).
    effect(() => {
      if (this.socket.connected()) this.refreshUnread();
    });
  }

  ngOnInit(): void {
    // Ouvre la connexion WS (idempotent) + bootstrap badge & liste preview.
    this.socket.start();
    this.refreshUnread();
    this.load();
    // Détecte le support push + l'état de permission (sans rien demander).
    void this.push.init();
  }

  /** Toggle push : active (demande permission + enregistre) ou désactive. */
  togglePush(): void {
    if (this.push.busy()) return;
    if (this.push.enabled()) {
      void this.push.disable();
    } else {
      void this.push.enable();
    }
  }

  toggleDropdown(): void {
    const next = !this.isOpen();
    this.isOpen.set(next);
    if (next) this.load();
  }

  closeDropdown(): void {
    this.isOpen.set(false);
  }

  load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.notifications
      .list({ page: 1, size: PREVIEW_SIZE })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data ?? []);
          this.loading.set(false);
        },
        error: () => {
          this.loading.set(false);
          this.error.set(this.locale.t('notif.loadFailed'));
        },
      });
  }

  /** Re-synchronise le badge depuis le compteur REST autoritatif. */
  private refreshUnread(): void {
    this.notifications
      .countUnread()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (n) => this.socket.setUnreadCount(n),
        error: () => {
          /* on garde la dernière valeur connue */
        },
      });
  }

  /** Clic sur une notif : marque lue (optimiste) puis navigue via le deeplink. */
  onItemClick(n: Notification): void {
    this.closeDropdown();
    if (!n.read) {
      this.items.update((list) =>
        list.map((x) => (x.id === n.id ? { ...x, read: true } : x)),
      );
      this.socket.decrementUnread();
      this.notifications
        .markRead(n.id)
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe({ error: () => { /* best-effort */ } });
    }
    const link = notificationLink(n);
    if (link) void this.router.navigate(link as unknown[]);
  }

  markAllRead(): void {
    this.notifications
      .markAllRead()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.items.update((list) => list.map((n) => ({ ...n, read: true })));
          this.socket.setUnreadCount(0);
        },
        error: () => { /* best-effort */ },
      });
  }

  /** Temps relatif court FR (ex. « 5 min », « 2 h », « 3 j »). */
  relativeTime(iso: string | null | undefined): string {
    if (!iso) return '';
    const then = serverDate(iso).getTime();
    if (Number.isNaN(then)) return '';
    const diff = Math.max(0, Date.now() - then);
    const min = Math.floor(diff / 60000);
    if (min < 1) return this.locale.t('time.now');
    if (min < 60) return this.locale.t('time.min', { n: min });
    const h = Math.floor(min / 60);
    if (h < 24) return this.locale.t('time.hour', { n: h });
    return this.locale.t('time.day', { n: Math.floor(h / 24) });
  }
}
